import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdaptiveLearningProgress } from "shared/hooks/useAdaptiveLearning";
import ConceptCard from "./components/ConceptCard";
import { SUBJECTS } from "shared/config/adaptiveLearningConfig";

/**
 * Adaptive Learning Dashboard
 * Displays all concepts with progress for the student
 */
const AdaptiveLearningDashboard = ({ onStartLearning }) => {
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const { progress, loading, error, refetch } = useAdaptiveLearningProgress(
    studentId,
    !!studentId
  );

  // Get student UUID from session storage (Keycloak user ID)
  useEffect(() => {
    try {
      // Use app_userprofile.id (Keycloak UUID) not app_role_userprofile.id (numeric DB ID)
      const userProfile = sessionStorage.getItem("app_userprofile");
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        setStudentId(profile.id);
      }
    } catch (err) {
      console.error("Error getting student UUID:", err);
    }
  }, []);

  // Filter concepts by subject and search query
  const filteredProgress = progress.filter((concept) => {
    const matchesSubject =
      selectedSubject === "ALL" || concept.subject === selectedSubject;
    const matchesSearch =
      !searchQuery ||
      concept.conceptName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      concept.conceptCode.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSubject && matchesSearch;
  });

  // Get unique subjects from progress
  const availableSubjects = [
    "ALL",
    ...new Set(progress.map((p) => p.subject)),
  ];

  // Calculate overall stats
  const totalConcepts = progress.length;
  const averageMastery = totalConcepts > 0
    ? progress.reduce((sum, p) => sum + (p.masteryPercentage || 0), 0) / totalConcepts
    : 0;
  const totalAttempts = progress.reduce((sum, p) => sum + (p.totalAttempts || 0), 0);
  const masteredConcepts = progress.filter((p) => p.progressTier === "MASTERED").length;

  const handleStartLearning = (conceptProgress) => {
    // If onStartLearning prop is provided (StudentHub mode), use it
    if (onStartLearning) {
      onStartLearning(conceptProgress);
    } else {
      // Otherwise use React Router navigation (standalone mode)
      navigate(`/adaptive-learning/session/${conceptProgress.conceptCode}`, {
        state: { conceptProgress },
      });
    }
  };

  if (!studentId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Student Profile Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please log in as a student to access Adaptive Learning.
          </p>
        </div>
      </div>
    );
  }

  if (loading && progress.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Loading your learning progress...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={refetch}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
            <span className="text-5xl">🎯</span>
            Adaptive Learning
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Master concepts at your own pace with AI-powered personalized learning
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-indigo-600">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {totalConcepts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Total Concepts
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-green-600">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {averageMastery.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Average Mastery
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-purple-600">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {totalAttempts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Total Attempts
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-yellow-600">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {masteredConcepts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Mastered
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Concepts
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <svg
                  className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Subject Filter */}
            <div className="md:w-64">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                {availableSubjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject === "ALL" ? "All Subjects" : subject}
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={refetch}
                disabled={loading}
                className="px-4 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <svg
                  className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Concepts Grid */}
        {filteredProgress.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-lg text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              No Concepts Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery || selectedSubject !== "ALL"
                ? "Try adjusting your filters"
                : "Start learning to see your progress here"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProgress.map((conceptProgress) => (
              <ConceptCard
                key={conceptProgress.id}
                progress={conceptProgress}
                onStartLearning={handleStartLearning}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdaptiveLearningDashboard;

